import { adminPost } from "@/lib/kbAdminClient";
import type { AdsActivityFreshAchievement } from "@/lib/adsAssistantClientEngagement";
import { useFreshAchievementsOptional } from "@/contexts/FreshAchievementsContext";
import type { GapInboxItem, GapSolutionResponse, PanelNote } from "./types";

type ShowNote = (note: PanelNote | null) => void;

type Params = {
  aiKbGapCandidates: any[];
  aiArticleLinkSuggestions: any[];
  editorName: string;
  articleSlug: string;
  showAiFeedback: ShowNote;
  showNote: ShowNote;
  setGapSaving: (v: boolean) => void;
  refreshGapInboxCount: () => Promise<void>;
  setGapInboxItems: (updater: any) => void;
  refreshGapInbox: (status?: string, testingOnlyArg?: boolean, pageOverride?: number, pageSizeOverride?: number) => Promise<void>;
  gapInboxStatusFilter: string;
  gapInboxTestingOnly: boolean;
  gapInboxPage: number;
  defaultWriteScope: "org" | "manufacturer" | "app";
  canPublishKb: boolean;
  setArticleTitle: (v: string) => void;
  setArticleContent: (v: string) => void;
  setArticleScope: (v: any) => void;
  setArticleAudience: (v: any) => void;
  setArticleDocType: (v: string) => void;
  setArticleTagsInput: (v: string) => void;
  setArticleSourceUrl: (v: string) => void;
  setArticleStatus: (v: any) => void;
  setLoadedArticle: (v: any) => void;
  setArticleSlug: (v: string) => void;
  setLastArticleId: (v: string) => void;
  setSelected: (v: any) => void;
  resetAiSession: () => void;
  setKbMode: (v: any) => void;
};

export function useKbGapInboxWorkflow(p: Params) {
  const fresh = useFreshAchievementsOptional();

  function publishFreshAchievements(rows?: AdsActivityFreshAchievement[]) {
    if (rows?.length && fresh) fresh.publish(rows);
  }

  async function saveGapsToInbox() {
    const gapItems = p.aiKbGapCandidates.map((item) => ({
      title: item.title, summary: item.summary, priority: item.priority, article_draft: item.article_draft, linked_article_slugs: item.linked_articles, item_type: "gap" as const,
    }));
    const linkItems = p.aiArticleLinkSuggestions.map((item) => ({
      title: item.title, summary: item.reason, priority: "medium" as const, article_draft: "", linked_article_slugs: [{ slug: item.slug, reason: item.reason, action: item.action }], item_type: "link_review" as const,
    }));
    const allItems = [...gapItems, ...linkItems];
    if (allItems.length === 0) return;
    p.setGapSaving(true);
    try {
      const response = await adminPost<{ ok: true; saved: number }>("/api/kb/admin/saveGaps", { items: allItems, submittedBy: p.editorName || undefined, sourceArticleSlug: p.articleSlug || undefined });
      if (response.ok) {
        p.showAiFeedback({ tone: "success", title: "Saved to gap inbox", text: `${response.data.saved} item(s) added to the KB Gap Inbox for follow-up.` });
        p.showNote({ tone: "success", title: "Saved to gap inbox", text: `${response.data.saved} item(s) saved. Open the KB Gap Inbox section below to review them.` });
        await p.refreshGapInboxCount();
      } else {
        p.showAiFeedback({ tone: "error", title: "Save to inbox failed", text: response.error });
      }
    } finally {
      p.setGapSaving(false);
    }
  }

  async function updateGapStatus(id: string, status: GapInboxItem["status"]) {
    const response = await adminPost<{ ok: true; item: any; fresh_achievements?: AdsActivityFreshAchievement[] }>(
      "/api/kb/admin/updateGap",
      { id, status },
    );
    if (response.ok) {
      publishFreshAchievements(response.data.fresh_achievements);
      p.setGapInboxItems((previous: GapInboxItem[]) => previous.map((item) => (item.id === id ? { ...item, status } : item)));
      await p.refreshGapInboxCount();
      await p.refreshGapInbox(p.gapInboxStatusFilter || undefined, p.gapInboxTestingOnly, p.gapInboxPage);
    } else {
      p.showNote({ tone: "error", title: "Status update failed", text: response.error });
    }
  }

  function addressGapItem(item: GapInboxItem) {
    void (async () => {
      const fallbackTitle = item.title.replace(/^\s*\[kb testing\]\s*/i, "").replace(/^\s*training thumbs-down:\s*/i, "").replace(/^\s*query miss:\s*/i, "").replace(/\s+/g, " ").trim() || "Knowledge Base Gap";
      let suggestedTitle = fallbackTitle.length > 72 ? `${fallbackTitle.slice(0, 71).trimEnd()}…` : fallbackTitle;
      const response = await adminPost<GapSolutionResponse>("/api/kb/admin/suggestGapSolution", { title: item.title, summary: item.summary, article_draft: item.article_draft, linked_article_slugs: item.linked_article_slugs });
      if (response.ok) {
        const fromAi = String(response.data.concise_title_suggestion || "").trim();
        if (fromAi) suggestedTitle = fromAi;
      }
      const selectedTitle = window.prompt("Suggested title for this new KB article (editable before opening editor):", suggestedTitle);
      if (selectedTitle == null) return;
      const finalTitle = selectedTitle.trim() || suggestedTitle;
      p.setArticleTitle(finalTitle);
      p.setArticleContent(item.article_draft);
      p.setArticleScope(p.defaultWriteScope);
      p.setArticleAudience("internal");
      p.setArticleDocType("");
      p.setArticleTagsInput("");
      p.setArticleSourceUrl("");
      p.setArticleStatus(p.canPublishKb ? "published" : "draft");
      p.setLoadedArticle(null);
      p.setArticleSlug("");
      p.setLastArticleId("");
      p.setSelected(null);
      p.resetAiSession();
      p.setKbMode("editor");
      p.showNote({ tone: "info", title: "Gap loaded into editor", text: `"${finalTitle}" was pre-loaded into the editor from the gap inbox. Edit the draft and save it when ready.` });
    })();
  }

  async function suggestGapSolution(item: GapInboxItem): Promise<GapSolutionResponse | null> {
    const response = await adminPost<GapSolutionResponse>("/api/kb/admin/suggestGapSolution", { title: item.title, summary: item.summary, article_draft: item.article_draft, linked_article_slugs: item.linked_article_slugs });
    if (!response.ok) {
      p.showNote({ tone: "error", title: "Suggest solution failed", text: response.error });
      return null;
    }
    return response.data;
  }

  async function resolveGapByLink(item: GapInboxItem, slug: string) {
    const result = await adminPost<{
      ok: true;
      item: { id: string; status: GapInboxItem["status"] };
      fresh_achievements?: AdsActivityFreshAchievement[];
    }>("/api/kb/admin/updateGap", { id: item.id, status: "done" });
    if (!result.ok) {
      p.showNote({ tone: "error", title: "Could not mark gap done", text: result.error });
      return;
    }
    publishFreshAchievements(result.data.fresh_achievements);
    p.setGapInboxItems((previous: GapInboxItem[]) => previous.map((entry) => (entry.id === item.id ? { ...entry, status: "done" } : entry)));
    await p.refreshGapInboxCount();
    await p.refreshGapInbox(p.gapInboxStatusFilter || undefined, p.gapInboxTestingOnly, p.gapInboxPage);
    p.showNote({ tone: "success", title: "Gap resolved by linking", text: `Marked this gap done and linked resolution to ${slug}.` });
  }

  return { saveGapsToInbox, updateGapStatus, addressGapItem, suggestGapSolution, resolveGapByLink };
}
